import { TFormalOffers } from '@helpers/FormalOffers/formalOffers.types';
import { getSortedFormalOffersByDurationDescending } from '@helpers/FormalOffers/getSortedFormalOffersByDurationDescending';
import { Radio, Text } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';

import { DurationLabel } from './DurationLabel';
import { LongestDurationOption } from './LongestDurationOption';
import { ShortestDurationOption } from './ShortestDurationOption';

export const ChooseDuration = ({
    formalOffers,
    selectedOfferId,
    drawdownAmount,
    onSelect
}: {
    formalOffers: TFormalOffers;
    selectedOfferId: string | undefined;
    drawdownAmount: number;
    onSelect: (offerId: string) => void;
}) => {
    const sortedOffers =
        getSortedFormalOffersByDurationDescending(formalOffers);

    if (sortedOffers.length <= 1) {
        return null;
    }

    return (
        <Radio.Group
            name="duration"
            label={
                <Text className="font-med">
                    <FormattedMessage
                        id="_.flexi_approved.duration_title"
                        defaultMessage="Choose your loan duration"
                    />
                </Text>
            }
            variant="frame"
            value={selectedOfferId}
            onValueChange={onSelect}
            data-testid="choose-duration-section"
        >
            <DurationOptions
                sortedOffers={sortedOffers}
                drawdownAmount={drawdownAmount}
            />
        </Radio.Group>
    );
};

const DurationOptions = ({
    sortedOffers,
    drawdownAmount
}: {
    sortedOffers: TFormalOffers;
    drawdownAmount: number;
}) => {
    const longest = sortedOffers[0];
    const shortest = sortedOffers[sortedOffers.length - 1];

    if (sortedOffers.length === 2) {
        return (
            <>
                <LongestDurationOption
                    offer={longest}
                    drawdownAmount={drawdownAmount}
                />
                <ShortestDurationOption
                    offer={shortest}
                    drawdownAmount={drawdownAmount}
                />
            </>
        );
    }

    if (sortedOffers.length === 3) {
        const middleDurationOffer = sortedOffers[1];

        return (
            <>
                <LongestDurationOption
                    offer={longest}
                    drawdownAmount={drawdownAmount}
                />
                <Radio.Root>
                    <Radio.Label className="!flex w-full flex-col gap-xs !font-reg">
                        <DurationLabel
                            offer={middleDurationOffer}
                            drawdownAmount={drawdownAmount}
                        />
                        <Text className="text-s text-structure-40">
                            <FormattedMessage
                                id="_.offer_choice_buttons.offer_description_middle"
                                defaultMessage="Balance your repayments and manage costs."
                            />
                        </Text>
                    </Radio.Label>
                    <Radio.Input
                        className="self-start"
                        value={middleDurationOffer.offer_id!}
                    />
                </Radio.Root>
                <ShortestDurationOption
                    offer={shortest}
                    drawdownAmount={drawdownAmount}
                />
            </>
        );
    }

    const middleDurationOffers = sortedOffers.slice(1, -1);

    return (
        <>
            <LongestDurationOption
                offer={longest}
                drawdownAmount={drawdownAmount}
            />
            {middleDurationOffers.map((offer) => (
                <Radio.Root key={offer.offer_id} variant="frame">
                    <Radio.Label className="!flex w-full flex-col gap-xs !font-reg">
                        <DurationLabel
                            offer={offer}
                            drawdownAmount={drawdownAmount}
                        />
                    </Radio.Label>
                    <Radio.Input
                        className="self-start"
                        value={offer.offer_id!}
                    />
                </Radio.Root>
            ))}
            <ShortestDurationOption
                offer={shortest}
                drawdownAmount={drawdownAmount}
            />
        </>
    );
};
